import { IonIcon } from "@ionic/react";
import { forwardRef, useRef } from "react";
import { type FieldApi } from "@tanstack/react-form";
import { closeOutline, informationCircleOutline } from "ionicons/icons";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { cn } from "@/lib/style/cva.config";
import { ReadOnlyBadge } from "./read-only-badge";

export type CustomFieldInputProps = {
  label: string;
  description?: string;
  required?: boolean;
  /** #10a: render disabled/read-only (employee may not edit this field). */
  readOnly?: boolean;
  type?: "text" | "number" | "date" | "email" | "tel";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: FieldApi<
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any
  >;
};

export const CustomFieldInput = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & CustomFieldInputProps
>(function CustomFieldInput(
  { label, description, required = false, readOnly = false, field, type = "text", ...props },
  ref,
) {
  const hasError = field.state.meta.errors && field.state.meta.errors.length > 0;
  // Extract error message - handle both string and ZodError object formats
  const errorMessage = hasError
    ? typeof field.state.meta.errors[0] === "string"
      ? field.state.meta.errors[0]
      : field.state.meta.errors[0]?.message || String(field.state.meta.errors[0])
    : null;
  const infoModalRef = useRef<HTMLIonModalElement>(null);

  return (
    <div className="flex flex-col gap-2">
      {/* Label */}
      <div className="flex items-center gap-1.5">
        <label htmlFor={field.name} className="text-md font-semibold text-txt-primary">
          {label}
          {required && <span className="text-error-120"> *</span>}
        </label>
        <ReadOnlyBadge readOnly={readOnly} />
        {description && (
          <button
            type="button"
            onClick={() => infoModalRef.current?.present()}
            className="flex h-6 w-6 items-center justify-center"
          >
            <IonIcon icon={informationCircleOutline} className="text-xl text-txt-tertiary" />
          </button>
        )}
      </div>

      {/* Input */}
      <input
        ref={ref}
        id={field.name}
        name={field.name}
        type={type}
        value={
          type === "number" && field.state.value !== null && field.state.value !== undefined
            ? field.state.value
            : (field.state.value ?? "")
        }
        onChange={(e) => {
          if (type === "number") {
            // Handle number input: empty string becomes null, otherwise parse as number
            const value = e.target.value === "" ? null : Number(e.target.value);
            field.handleChange(value);
          } else {
            field.handleChange(e.target.value);
          }
        }}
        onBlur={field.handleBlur}
        readOnly={readOnly}
        disabled={readOnly || field.state.meta.isValidating || field.form.state.isSubmitting}
        className={cn(
          "w-full rounded-lg border border-bordercolor-primary bg-white px-4 py-[13px] text-17 font-medium leading-tight text-txt-primary",
          "placeholder:text-txt-tertiary",
          "focus:border-brand-primary focus:ring-brand-primary focus:outline-none focus:ring-1",
          "disabled:cursor-not-allowed disabled:opacity-50",
          hasError && "border-error-100 focus:border-error-100 focus:ring-error-100",
          props.className,
        )}
        {...props}
      />

      {/* Error message */}
      {hasError && (
        <p className="text-sm font-medium leading-[1.4] text-error-120">{errorMessage}</p>
      )}

      {/* Info Modal */}
      {description && (
        <ModalSheetAutoHeight ref={infoModalRef}>
          <div className="flex max-h-[80vh] flex-col gap-5 px-6 pb-6 pt-4">
            {/* Header with title and close */}
            <div className="flex shrink-0 items-center justify-between">
              <h2 className="text-lg font-semibold leading-[1.6] text-txt-primary">{label}</h2>
              <button
                type="button"
                onClick={() => infoModalRef.current?.dismiss()}
                className="flex size-12 items-center justify-center"
              >
                <IonIcon icon={closeOutline} className="text-2xl text-txt-primary" />
              </button>
            </div>

            {/* Content - properly handles whitespace with whitespace-pre-line */}
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <p className="whitespace-pre-line text-md font-medium leading-[1.5] text-txt-tertiary">
                {description}
              </p>
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={() => infoModalRef.current?.dismiss()}
              className="w-full shrink-0 rounded-xl bg-button-primary py-3 text-center text-md font-semibold text-txt-inverted"
            >
              Begrepen
            </button>
          </div>
        </ModalSheetAutoHeight>
      )}
    </div>
  );
});
